import { useState, useEffect } from "react";
import EntityManager from "@/pages/admin/EntityManager";
import SectionFields from "@/pages/admin/SectionFields";
import { SECTION_SCHEMA } from "@/pages/admin/schema";
import { api } from "@/lib/api";

export function CourseManager({ lang, draft, setDraft, setLang }) {
  const libVal = (draft?.content?.[lang]?.library) || {};
  const setLib = (patch) =>
    setDraft((d) => ({
      ...d,
      content: { ...d.content, [lang]: { ...d.content[lang], library: { ...(d.content[lang].library || {}), ...patch } } },
    }));
  return (
    <div className="space-y-12">
      {draft && (
        <div className="border border-ink/15 p-6" data-testid="library-texts-editor">
          <div className="flex items-center justify-between mb-5">
            <div className="eyebrow">Texte pagină /library ({lang.toUpperCase()})</div>
            {setLang && (
              <div className="flex gap-1">
                <button type="button" onClick={() => setLang("ro")} data-testid="lib-lang-ro" className={`eyebrow px-3 py-1.5 border ${lang === "ro" ? "bg-ink text-paper border-ink" : "border-ink/25"}`}>RO</button>
                <button type="button" onClick={() => setLang("en")} data-testid="lib-lang-en" className={`eyebrow px-3 py-1.5 border ${lang === "en" ? "bg-ink text-paper border-ink" : "border-ink/25"}`}>EN</button>
              </div>
            )}
          </div>
          <SectionFields fields={SECTION_SCHEMA.library} value={libVal} onChange={setLib} testidPrefix="library." />
          <p className="eyebrow text-ink/40 mt-4">Salvează cu butonul „Salvează" de sus pentru a aplica.</p>
        </div>
      )}

      <EntityManager
        title="Library — videoclipuri"
        endpoint="/courses"
        lang={lang}
        setLang={setLang}
        sideBySide
        addLabel="Adaugă video"
        emptyItem={{
          slug: "", slug_en: "", slug_ro: "", title_ro: "", title_en: "", subtitle_ro: "", subtitle_en: "",
          description_ro: "", description_en: "", youtube_url: "", cover_image: "", zone: "perspectives",
          bullets_ro: [], bullets_en: [], lessons: [],
          manifesto_label_ro: "", manifesto_label_en: "", body_ro: "", body_en: "",
          seo_title_ro: "", seo_title_en: "", meta_description_ro: "", meta_description_en: "",
          order: 0, published: true,
        }}
        fields={[
          { k: "zone", label: "Zonă Afișare", type: "select", options: [
            { value: "perspectives", label: "The Perspectives" },
            { value: "masterclasses", label: "The Masterclasses" },
            { value: "dialogues", label: "The Dialogues" },
          ] },
          { k: "slug", label: "Slug (identificator)", type: "text", required: true },
          { k: "slug_en", label: "URL Engleză", type: "text" },
          { k: "slug_ro", label: "URL Română", type: "text" },
          { k: "title", label: "Titlu", type: "bi", required: true },
          { k: "subtitle", label: "Subtitlu", type: "bi" },
          { k: "description", label: "Descriere (scurtă)", type: "bi-area", big: true },
          { k: "youtube_url", label: "Link YouTube (intro)", type: "text" },
          { k: "cover_image", label: "Copertă", type: "image" },
          { k: "bullets", label: "Ce înveți", type: "bi-list" },
          { k: "manifesto_label", label: "Etichetă secțiune Manifest (ex: Manifestul cursului)", type: "bi" },
          { k: "body", label: "Manifestul cursului (text amplu)", type: "bi-area", big: true },
          { k: "lessons", label: "Lecții (titlu + video + thumbnail)", type: "lessons" },
          { k: "seo_title", label: "SEO Title", type: "bi" },
          { k: "meta_description", label: "Meta Description", type: "bi-area" },
          { k: "order", label: "Ordine", type: "number" },
          { k: "published", label: "Publicat", type: "bool" },
        ]}
      />
    </div>
  );
}

export function ProjectManager({ lang, setLang }) {
  return (
    <EntityManager
      title="Proiecte"
      endpoint="/projects"
      lang={lang}
      setLang={setLang}
      addLabel="Adaugă proiect"
      emptyItem={{
        slug: "", slug_en: "", slug_ro: "", title_ro: "", title_en: "", category_ro: "", category_en: "",
        year: "", role_ro: "", role_en: "", cover_image: "", gallery: [],
        intro_ro: "", intro_en: "", narrative_ro: "", narrative_en: "",
        achievements_ro: [], achievements_en: [],
        seo_title_ro: "", seo_title_en: "", meta_description_ro: "", meta_description_en: "",
        order: 0, published: true,
      }}
      fields={[
        { k: "slug", label: "Slug (identificator)", type: "text", required: true },
        { k: "slug_en", label: "URL Engleză", type: "text" },
        { k: "slug_ro", label: "URL Română", type: "text" },
        { k: "title", label: "Titlu", type: "bi", required: true },
        { k: "category", label: "Categorie", type: "bi" },
        { k: "year", label: "An", type: "text" },
        { k: "role", label: "Rol", type: "bi" },
        { k: "cover_image", label: "Copertă", type: "image" },
        { k: "intro", label: "Introducere", type: "bi-area" },
        { k: "narrative", label: "Narațiune", type: "bi-area", big: true },
        { k: "achievements", label: "Realizări", type: "bi-list" },
        { k: "gallery", label: "Galerie", type: "image-list" },
        { k: "seo_title", label: "SEO Title", type: "bi" },
        { k: "meta_description", label: "Meta Description", type: "bi-area" },
        { k: "order", label: "Ordine", type: "number" },
        { k: "published", label: "Publicat", type: "bool" },
      ]}
    />
  );
}

export function MediaManager({ lang, setLang }) {
  return (
    <EntityManager
      title="Media"
      endpoint="/media"
      lang={lang}
      setLang={setLang}
      addLabel="Adaugă media"
      homeCount={3}
      emptyItem={{
        title_ro: "", title_en: "", outlet_ro: "", outlet_en: "", kind_ro: "", kind_en: "",
        youtube_url: "", external_url: "", cover_image: "", order: 0, published: true,
      }}
      fields={[
        { k: "title", label: "Titlu", type: "bi", required: true },
        { k: "outlet", label: "Sursă / Outlet", type: "bi" },
        { k: "kind", label: "Tip (TV, Podcast, Speaking)", type: "bi" },
        { k: "youtube_url", label: "Link YouTube", type: "text" },
        { k: "external_url", label: "Link extern (dacă nu e video)", type: "text" },
        { k: "cover_image", label: "Copertă", type: "image" },
        { k: "order", label: "Ordine", type: "number" },
        { k: "published", label: "Publicat", type: "bool" },
      ]}
    />
  );
}


const SEO_PAGES = [
  { key: "home", label: "Home" },
  { key: "about", label: "Despre" },
  { key: "services", label: "Servicii" },
  { key: "journal", label: "Jurnal" },
  { key: "portfolio", label: "Portofoliu" },
  { key: "library", label: "Cursuri" },
];

const seoInput =
  "w-full bg-transparent border border-ink/25 px-3 py-2 outline-none focus:border-ink transition-colors font-light text-sm";

function UrlRegistry() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get("/seo/urls").then((r) => setData(r.data)).catch(() => setError(true));
  }, []);

  const groups = [
    { type: "page", label: "Pagini fixe" },
    { type: "essay", label: "Jurnal (eseuri)" },
    { type: "course", label: "Library (cursuri)" },
    { type: "project", label: "Portofoliu (proiecte)" },
  ];
  const total = data ? data.records.reduce((n, r) => n + (r.en !== r.ro ? 2 : 1), 0) : 0;

  return (
    <div className="mt-14 pt-10 border-t border-ink/15" data-testid="seo-url-registry">
      <div className="flex items-baseline justify-between mb-2">
        <div className="font-display text-2xl">Evidență URL-uri</div>
        {data && <div className="eyebrow text-ink/40 tnum" data-testid="url-registry-count">{total} URL-uri</div>}
      </div>
      <p className="text-sm text-ink/55 mb-6">
        Toate URL-urile publice generate automat, incluse în{" "}
        <a className="underline" href="/api/sitemap.xml" target="_blank" rel="noreferrer">sitemap.xml</a>{" "}
        (cu hreflang RO/EN). Bază: <span className="tnum">{data?.base}</span>
      </p>
      {error && <div className="text-sm text-red-600" data-testid="url-registry-error">Nu am putut încărca lista.</div>}
      {!data && !error && <div className="eyebrow text-ink/40">Se încarcă…</div>}
      {data && (
        <div className="space-y-8">
          {groups.map((g) => {
            const items = data.records.filter((r) => r.type === g.type);
            if (items.length === 0) return null;
            return (
              <div key={g.type} data-testid={`url-group-${g.type}`}>
                <div className="eyebrow text-ink/45 mb-3">{g.label} · {items.length}</div>
                <div className="space-y-2">
                  {items.map((r, i) => (
                    <div key={i} className="border border-ink/12 px-3 py-2 text-sm">
                      <div className="font-medium mb-1 truncate">{r.title}</div>
                      <div className="flex flex-col gap-0.5">
                        <a className="text-ink/70 hover:text-ink underline decoration-ink/20 break-all" href={r.ro} target="_blank" rel="noreferrer">RO · {r.ro}</a>
                        {r.en !== r.ro && (
                          <a className="text-ink/70 hover:text-ink underline decoration-ink/20 break-all" href={r.en} target="_blank" rel="noreferrer">EN · {r.en}</a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function SeoManager({ draft, setDraft, lang, setLang }) {
  const seo = draft.content?.[lang]?.seo || {};
  const setSeo = (page, k, v) =>
    setDraft((d) => ({
      ...d,
      content: {
        ...d.content,
        [lang]: {
          ...d.content[lang],
          seo: {
            ...(d.content[lang].seo || {}),
            [page]: { ...((d.content[lang].seo || {})[page] || {}), [k]: v },
          },
        },
      },
    }));

  return (
    <div className="max-w-3xl" data-testid="seo-manager">
      <div className="flex items-center justify-between mb-4">
        <div className="eyebrow text-ink/50">SEO în limba:</div>
        <div className="flex items-center gap-1 eyebrow border border-ink/25">
          <button onClick={() => setLang("ro")} data-testid="seo-lang-ro" className={`px-4 py-2 ${lang === "ro" ? "bg-ink text-paper" : ""}`}>RO</button>
          <button onClick={() => setLang("en")} data-testid="seo-lang-en" className={`px-4 py-2 ${lang === "en" ? "bg-ink text-paper" : ""}`}>EN</button>
        </div>
      </div>
      <p className="text-sm text-ink/55 mb-6">
        SEO Title și Meta Description suprascriu automat tag-urile din &lt;head&gt;, canonical/hreflang și preview-ul social (LinkedIn / Open Graph). SEO-ul pentru articole, cursuri și proiecte se editează în tab-urile lor.
      </p>
      <div className="space-y-5">
        {SEO_PAGES.map((p) => {
          const v = seo[p.key] || {};
          return (
            <div key={p.key} className="border border-ink/20 p-4" data-testid={`seo-page-${p.key}`}>
              <div className="font-display text-xl mb-3">{p.label}</div>
              <input
                className={seoInput + " mb-2"}
                placeholder="SEO Title"
                value={v.title || ""}
                onChange={(e) => setSeo(p.key, "title", e.target.value)}
                data-testid={`seo-title-${p.key}`}
              />
              <textarea
                rows={2}
                className={seoInput}
                placeholder="Meta Description"
                value={v.description || ""}
                onChange={(e) => setSeo(p.key, "description", e.target.value)}
                data-testid={`seo-desc-${p.key}`}
              />
            </div>
          );
        })}
      </div>
      <UrlRegistry />
    </div>
  );
}
